/**
 * Cooperative open/close animation for the WordPress core/accordion block.
 *
 * Watches the `is-open` class on `.wp-block-accordion-item` (toggled by the
 * Interactivity API via `data-wp-class--is-open`) and runs a coupled
 * max-height + margin-block-start transition on the contained panel. Pairs
 * with `accordion-animation.css`, which un-hides the inert panel only while
 * `.is-animating` is present so the transition can play out. After
 * `transitionend` we remove `.is-animating` and core's `display:none` (and
 * the a11y semantics of `inert`) resumes.
 *
 * Honors `prefers-reduced-motion: reduce`.
 */

( function () {
	const ITEM_SELECTOR = '.wp-block-accordion-item';
	const PANEL_SELECTOR = '.wp-block-accordion-panel';
	const OPEN_CLASS = 'is-open';
	const ANIMATING_CLASS = 'is-animating';

	function prefersReducedMotion() {
		return (
			typeof window.matchMedia === 'function' &&
			window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches
		);
	}

	/**
	 * Direct child panel only — the markup is `item > heading + panel`,
	 * so we never want to grab a nested accordion's panel.
	 *
	 * @param {Element} item
	 * @return {HTMLElement|null}
	 */
	function getPanel( item ) {
		const panel = item.querySelector( ':scope > ' + PANEL_SELECTOR );
		return panel instanceof HTMLElement ? panel : null;
	}

	/**
	 * Run a coupled max-height + margin-block-start transition.
	 *
	 * Why both: core's `.wp-block-accordion-panel[inert]` rule also sets
	 * `margin-block-start: 0`, so the heading-to-panel gap (from theme.json
	 * `spacing.blockGap` / `--wp--style--block-gap`) would snap on toggle.
	 * The companion CSS pins the gap while `.is-animating` is present; this
	 * function animates the inline `margin-block-start` alongside `max-height`
	 * so the gap collapses on close / expands on open.
	 *
	 * @param {HTMLElement} panel
	 * @param {boolean}     opening
	 */
	function animate( panel, opening ) {
		if ( prefersReducedMotion() ) {
			return;
		}

		// Cancel any in-flight animation cleanly before starting a new one.
		panel.style.transition = 'none';
		panel.classList.add( ANIMATING_CLASS );

		// Force layout flush so the next style change is observed as a transition.
		const targetHeight = panel.scrollHeight;

		if ( opening ) {
			// Start: collapsed (height 0, no gap). End: natural height + cascade gap.
			panel.style.maxHeight = '0px';
			panel.style.marginBlockStart = '0px';
		} else {
			// Start: natural height + pinned gap (from .is-animating[inert] CSS rule).
			// End: collapsed (height 0, no gap).
			panel.style.maxHeight = targetHeight + 'px';
		}

		// Re-enable transitions on the next frame, then set the end state.
		requestAnimationFrame( function () {
			panel.style.transition = '';
			requestAnimationFrame( function () {
				if ( opening ) {
					panel.style.maxHeight = targetHeight + 'px';
					// Clear inline margin so the cascade (block-gap) takes over.
					panel.style.marginBlockStart = '';
				} else {
					panel.style.maxHeight = '0px';
					panel.style.marginBlockStart = '0px';
				}
			} );
		} );

		function cleanup( event ) {
			if ( event && event.propertyName !== 'max-height' ) {
				return;
			}
			panel.style.maxHeight = '';
			panel.style.marginBlockStart = '';
			panel.style.transition = '';
			panel.classList.remove( ANIMATING_CLASS );
			panel.removeEventListener( 'transitionend', cleanup );
			panel.removeEventListener( 'transitioncancel', cleanup );
		}

		panel.addEventListener( 'transitionend', cleanup );
		panel.addEventListener( 'transitioncancel', cleanup );
	}

	/**
	 * @param {MutationRecord[]} mutations
	 */
	function handleMutations( mutations ) {
		for ( let i = 0; i < mutations.length; i++ ) {
			const m = mutations[ i ];
			if ( m.type !== 'attributes' || m.attributeName !== 'class' ) {
				continue;
			}

			const item = m.target;
			if ( ! ( item instanceof HTMLElement ) ) {
				continue;
			}
			if ( ! item.matches( ITEM_SELECTOR ) ) {
				continue;
			}

			const wasOpen = m.oldValue
				? m.oldValue.split( /\s+/ ).indexOf( OPEN_CLASS ) !== -1
				: false;
			const isOpen = item.classList.contains( OPEN_CLASS );
			if ( wasOpen === isOpen ) {
				continue;
			}

			const panel = getPanel( item );
			if ( ! panel ) {
				continue;
			}

			animate( panel, isOpen );
		}
	}

	function boot() {
		// One observer covers all current and future accordions on the page
		// (e.g. content swapped in by query loops or modal patterns).
		const observer = new MutationObserver( handleMutations );
		observer.observe( document.body, {
			subtree: true,
			attributes: true,
			attributeFilter: [ 'class' ],
			attributeOldValue: true,
		} );
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', boot, { once: true } );
	} else {
		boot();
	}
} )();